/**
 * "This draft exists but is never used."
 *
 * When a Gemeinde already has a verified pack in the repo, an ingested draft is
 * shadowed: readable, but no run touches it. That is a genuinely confusing
 * state — someone spends an hour verifying rows and none of it reaches a check
 * — so it is said in full sentences at the top of both surfaces where the draft
 * appears, rather than as a quiet badge.
 *
 * Grey and dashed like every other non-verified surface. Nothing here is a
 * warning colour: nothing has gone wrong, the state is just not what the reader
 * assumes.
 */
import { Layers } from 'lucide-react'

import { Icon } from '@/components/Icon'
import { useI18n } from '@/i18n'
import type { PackConflict } from '@/api/types'

export function ShadowedNote({ conflicts }: { conflicts: readonly PackConflict[] }) {
  const { t } = useI18n()
  return (
    <div
      role="note"
      data-testid="shadowed-note"
      className="flex items-start gap-3 rounded-sm border border-dashed border-border-strong bg-surface-2 px-4 py-3 text-[13px] text-muted"
    >
      <Icon icon={Layers} className="mt-0.5" />
      <div className="flex flex-col gap-1">
        <p className="font-medium text-ink-2">{t('rulebase.shadowedTitle')}</p>
        <p>{t('rulebase.shadowedLead')}</p>
        {conflicts.length > 0 ? (
          <div className="flex flex-col gap-1 text-[12px] text-muted-2">
            <p className="font-medium">{t('rulebase.conflicts')}</p>
            <ul className="flex flex-col gap-0.5">
              {conflicts.map((conflict, index) => (
                <li key={`${conflict.kind}-${conflict.zone ?? ''}-${conflict.key ?? ''}-${index}`}>
                  {conflict.message_de}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  )
}
